import { Injectable } from '@angular/core';
import type { AnalyticsEventPayload, GtmWindow } from './analytics.types';

/**
 * Centralized GTM dataLayer publisher for ESO Lite analytics events (OM-807).
 * Components must use this service instead of pushing to `window.dataLayer` directly.
 * Failures never throw so analytics outages cannot interrupt shipment workflows.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  /**
   * Pushes an approved analytics event payload onto `window.dataLayer`.
   *
   * @param payload - Event name plus approved parameters only (no sensitive shipment data).
   */
  trackEvent(payload: AnalyticsEventPayload): void {
    this.push(payload);
  }

  /**
   * Publishes a sanitized ErrorEvent for service or validation failures.
   * `classification` must be a stable code — never raw HTTP bodies or unrestricted messages.
   *
   * @param serviceName - Logical service or API operation name.
   * @param classification - Approved error classification or code.
   */
  trackError(serviceName: string, classification: string): void {
    this.push({
      event: 'ErrorEvent',
      ServiceName: serviceName,
      Message: classification,
    });
  }

  /**
   * Publishes client-observed service duration as ServicePerformance.
   *
   * @param serviceName - Logical service or API operation name.
   * @param timeDifferenceInSec - Elapsed time in seconds.
   */
  trackServicePerformance(serviceName: string, timeDifferenceInSec: number): void {
    this.push({
      event: 'ServicePerformance.',
      serviceName,
      timeDifferenceInSec,
    });
  }

  /**
   * Ensures dataLayer exists and pushes without throwing on blocked or missing GTM.
   */
  private push(payload: AnalyticsEventPayload): void {
    try {
      const gtmWindow = globalThis.window as GtmWindow | undefined;
      if (!gtmWindow) {
        return;
      }
      const dataLayer = gtmWindow.dataLayer ?? (gtmWindow.dataLayer = []);
      dataLayer.push(payload);
    } catch {
      // Analytics must never interrupt the application.
    }
  }
}
